import React, { useState, useMemo } from 'react';
import { 
  Layers, CheckSquare, Square, Sparkles, Check, 
  ChevronDown, ChevronUp, Package, ArrowRight, RefreshCw, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'react-hot-toast';

const fmt = (n) => new Intl.NumberFormat('id-ID', { maximumFractionDigits: 0 }).format(Math.round(n || 0));

export default function BatchRepeatOrderPicker({
  brandList = [],
  brandName = '',
  onAddBatchProducts,
  currentProducts = [],
  itemTambahanList = []
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedVariants, setSelectedVariants] = useState({}); 
  // format: { [variantId]: { selected: boolean, selectedSize: string, qty: number|string } }
  const [bulkQty, setBulkQty] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // Inisialisasi default state untuk setiap varian jika belum ada
  const variantStates = useMemo(() => {
    const states = {};
    brandList.forEach(v => {
      const existing = selectedVariants[v.id];
      const defaultSize = v.sizes && v.sizes.length > 0 ? String(v.sizes[0].size) : '';
      states[v.id] = {
        selected: existing ? existing.selected : false,
        selectedSize: existing?.selectedSize || defaultSize,
        qty: existing?.qty !== undefined ? existing.qty : ''
      };
    });
    return states;
  }, [brandList, selectedVariants]);

  const filteredBrandList = useMemo(() => {
    if (!searchFilter.trim()) return brandList;
    const q = searchFilter.toLowerCase();
    return brandList.filter(v => (v.nama_product || '').toLowerCase().includes(q));
  }, [brandList, searchFilter]);

  const selectedCount = Object.values(variantStates).filter(s => s.selected).length;
  const isAllSelected = brandList.length > 0 && selectedCount === brandList.length;

  const toggleSelect = (variantId) => {
    setSelectedVariants(prev => {
      const current = variantStates[variantId];
      return {
        ...prev,
        [variantId]: {
          ...current,
          selected: !current.selected
        }
      };
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      // Unselect all
      const updated = {};
      brandList.forEach(v => {
        updated[v.id] = { ...variantStates[v.id], selected: false };
      });
      setSelectedVariants(updated);
    } else {
      // Select all
      const updated = {};
      brandList.forEach(v => {
        updated[v.id] = { ...variantStates[v.id], selected: true };
      });
      setSelectedVariants(updated);
    }
  };

  const setSizeForVariant = (variantId, sizeStr) => {
    setSelectedVariants(prev => ({
      ...prev,
      [variantId]: {
        ...variantStates[variantId],
        selectedSize: String(sizeStr)
      }
    }));
  };

  const setQtyForVariant = (variantId, qtyVal) => {
    setSelectedVariants(prev => ({
      ...prev,
      [variantId]: {
        ...variantStates[variantId],
        qty: qtyVal
      }
    }));
  };

  const applyBulkQty = () => {
    if (!bulkQty) return;
    const cleanQty = parseInt(bulkQty, 10) || 0;
    if (cleanQty <= 0) return;

    const updated = { ...selectedVariants };
    brandList.forEach(v => {
      if (variantStates[v.id]?.selected) {
        updated[v.id] = {
          ...variantStates[v.id],
          qty: cleanQty
        };
      }
    });
    setSelectedVariants(updated);
    toast.success(`Qty ${cleanQty} pcs diterapkan ke ${selectedCount} varian terpilih!`);
  };

  const handleApplyToInvoice = () => {
    const selectedItems = brandList.filter(v => variantStates[v.id]?.selected);
    if (selectedItems.length === 0) {
      toast.error('Pilih minimal satu varian terlebih dahulu!');
      return;
    }

    const newProductCards = selectedItems.map(varian => {
      const state = variantStates[varian.id];
      const isDiscoverySet = (varian.nama_product || '').toLowerCase().includes('discovery set');

      if (isDiscoverySet) {
        const mappedItems = (varian.sizes || []).filter(s => s.racikan && s.racikan.length > 0).map(s => ({
          nama_variant: s.size,
          botol_id: s.botol_id || null,
          tutup_id: s.tutup_id || null,
          spray_id: s.spray_id || null,
          botol_dari_client: s.botol_dari_client,
          botol_ukuran_client: s.botol_ukuran_client,
          is_fullset: s.is_fullset || false,
          fullset_id: s.fullset_id || null,
          fullset_nama: s.fullset_nama || null,
          racikan: s.racikan || [],
        }));
        
        const mappedKomponen = (varian.sizes || []).filter(s => (!s.racikan || s.racikan.length === 0) && s.bahan).map(s => {
          const kName = s.bahan?.nama_bahan || s.size;
          const match = itemTambahanList.find(x => x.nama === kName);
          return {
            nama: match ? match.nama : kName,
            item_nama: match ? match.nama : kName,
            inventory_item_id: match ? match.id : null,
            kategori: match ? match.kategori : (s.bahan?.kategori_bahan || 'Box/Kemasan'),
            harga: match?.harga_rupiah || s.bahan?.custom_hpp || s.bahan?.harga_rupiah || 0,
            harga_jual: match?.harga_jual || s.bahan?.custom_harga_jual || s.bahan?.harga_jual || 0,
            qty: 1
          };
        }).filter(k => k.harga > 0 || k.harga_jual > 0);
        
        const attachedKomponen = (varian.sizes || []).flatMap(s => s.komponen_tambahan || []).map(k => {
          const match = itemTambahanList.find(x => x.nama === (k.nama || k.item_nama));
          return {
            ...k,
            inventory_item_id: match ? match.id : null,
            harga: match?.harga_rupiah || k.harga || 0,
            harga_jual: match?.harga_jual || k.harga_jual || 0,
          };
        });
        const finalKomponen = [...mappedKomponen, ...attachedKomponen];

        return {
          nama_product: varian.nama_product,
          qty: state.qty || '',
          variant_id: varian.id,
          is_discovery_set: true,
          items: mappedItems,
          komponen_tambahan: finalKomponen,
          jasa_nominal: undefined,
          harga_jual_per_pcs: undefined,
          botol_id: null,
          tutup_id: null,
          spray_id: null,
          fullset_id: null,
          is_fullset: false,
          botol_dari_client: false,
          botol_ukuran_client: 0,
          racikan: [],
          selected_size: 0,
          custom_ukuran_ml: undefined,
          custom_ukuran_aktual_ml: undefined
        };
      } else {
        const sizeObj = (varian.sizes || []).find(s => String(s.size) === String(state.selectedSize)) || (varian.sizes && varian.sizes[0]);
        return {
          nama_product: varian.nama_product,
          qty: state.qty || '',
          variant_id: varian.id,
          is_discovery_set: false,
          selected_size: sizeObj?.size || 0,
          botol_id: sizeObj?.botol_id || null,
          tutup_id: sizeObj?.tutup_id || null,
          spray_id: sizeObj?.spray_id || null,
          botol_dari_client: sizeObj ? sizeObj.botol_dari_client : true,
          botol_ukuran_client: sizeObj ? sizeObj.botol_ukuran_client : 0,
          is_fullset: sizeObj?.is_fullset || false,
          fullset_id: sizeObj?.fullset_id || null,
          fullset_nama: sizeObj?.fullset_nama || null,
          racikan: sizeObj?.racikan || [],
          komponen_tambahan: sizeObj?.komponen_tambahan || [],
          jasa_nominal: undefined,
          harga_jual_per_pcs: undefined
        };
      }
    });

    // Cek jika produk saat ini hanya ada 1 dan masih kosong, kita gantikan
    const isCurrentFirstEmpty = currentProducts.length === 1 && !currentProducts[0].nama_product && (!currentProducts[0].racikan || currentProducts[0].racikan.length === 0);
    
    onAddBatchProducts(newProductCards, isCurrentFirstEmpty);
    toast.success(`✨ Berhasil memasukkan ${newProductCards.length} varian ke form invoice!`);
  };

  if (brandList.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-[#FFFDF9] to-[#FAF5EC] border-2 border-[#D4AF37]/80 rounded-2xl p-5 shadow-md mb-6 transition-all duration-200">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-[#E6D5B8]/80">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8C6208] text-white flex items-center justify-center shadow-xs">
            <Zap className="w-5 h-5 fill-current" />
          </div>
          <div>
            <h4 className="font-extrabold text-sm sm:text-base text-[#302621] flex items-center gap-2">
              Pilih Sekaligus Varian {brandName}
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                {brandList.length} Varian Tersedia
              </span>
            </h4>
            <p className="text-xs text-[#7A695B]">
              Centang varian & ukuran yang ingin di-repeat order kali ini, lalu klik Masukkan ke Form.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            type="button"
            size="sm"
            onClick={handleSelectAll}
            className={`text-xs font-bold px-3.5 h-8 rounded-xl flex items-center gap-1.5 transition-all shadow-xs ${
              isAllSelected
                ? 'bg-[#EAE1D1] text-[#4A3B32] hover:bg-[#DDD2C0] border border-[#C5B79F]'
                : 'bg-[#8C6208] text-white hover:bg-[#6F4E06]'
            }`}
          >
            {isAllSelected ? (
              <>
                <Square className="w-3.5 h-3.5" />
                Batal Pilih Semua
              </>
            ) : (
              <>
                <CheckSquare className="w-3.5 h-3.5" />
                Pilih Semua ({brandList.length})
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsOpen(!isOpen)}
            className="h-8 w-8 p-0 text-[#8C6208] hover:bg-[#EAE1D1]/50 rounded-lg"
          >
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {isOpen && (
        <div className="pt-4 space-y-4">
          
          {/* Quick Filter & Bulk Qty Setter */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-[#E6D5B8]/80">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Cari varian di daftar ini..."
                className="w-full text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-hidden focus:border-[#D4AF37]"
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-[#7A695B] shrink-0">Set Qty Seragam:</span>
              <input
                type="number"
                min="1"
                placeholder="Contoh: 500"
                value={bulkQty}
                onChange={(e) => setBulkQty(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyBulkQty();
                  }
                }}
                className="w-24 text-xs font-bold text-center bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-hidden focus:border-[#D4AF37]"
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={applyBulkQty}
                disabled={selectedCount === 0}
                className="text-xs font-bold h-8 border-[#D4AF37] text-[#8C6208] hover:bg-[#FAF6EE] rounded-lg shrink-0"
              >
                Terapkan
              </Button>
            </div>
          </div>

          {/* Variants Grid List */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto pr-1">
            {filteredBrandList.map((v) => {
              const state = variantStates[v.id] || {};
              const isSelected = state.selected;
              const hasSizes = v.sizes && v.sizes.length > 0;
              const isDiscovery = (v.nama_product || '').toLowerCase().includes('discovery set');

              return (
                <div
                  key={v.id}
                  onClick={() => toggleSelect(v.id)}
                  className={`p-3 rounded-xl border transition-all duration-150 cursor-pointer flex flex-col justify-between gap-2.5 select-none ${
                    isSelected
                      ? 'bg-gradient-to-r from-[#FFFDF9] to-[#FDF8EE] border-[#D4AF37] shadow-xs ring-1 ring-[#D4AF37]/30'
                      : 'bg-white border-[#E6D5B8]/70 hover:border-[#D4AF37]/60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-[#8C6208] text-white' : 'border border-[#C8B89E]'
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                      </div>
                      <div>
                        <p className={`font-bold text-xs sm:text-sm ${isSelected ? 'text-[#302621]' : 'text-[#4A3B32]'}`}>
                          {v.nama_product}
                        </p>
                        {isDiscovery && (
                          <span className="text-[9px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                            Discovery Set ({v.sizes?.length || 0} items)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Qty Input Per Item */}
                    <div 
                      className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-200 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="text-[10px] text-[#7A695B] font-medium">Qty:</span>
                      <input
                        type="number"
                        min="1"
                        placeholder="Pcs"
                        value={state.qty || ''}
                        onChange={(e) => {
                          setQtyForVariant(v.id, e.target.value);
                          if (!state.selected) toggleSelect(v.id);
                        }}
                        className="w-16 text-xs text-center font-bold bg-transparent border-none p-0 focus:ring-0 text-[#302621]"
                      />
                    </div>
                  </div>

                  {/* Size Selector Radio / Pills */}
                  {hasSizes && !isDiscovery && (
                    <div 
                      className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-slate-100"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="text-[10px] font-semibold text-[#8C7A6B]">Ukuran:</span>
                      {v.sizes.map((s, sIdx) => {
                        const isCurrentSize = String(s.size) === String(state.selectedSize);
                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() => {
                              setSizeForVariant(v.id, s.size);
                              if (!state.selected) toggleSelect(v.id);
                            }}
                            className={`text-[11px] font-bold px-2.5 py-0.5 rounded-md border transition-all ${
                              isCurrentSize
                                ? 'bg-[#8C6208] text-white border-[#8C6208] shadow-2xs'
                                : 'bg-white text-[#5A4B40] border-slate-200 hover:border-[#D4AF37]'
                            }`}
                          >
                            {s.size}{!String(s.size).toLowerCase().includes('ml') ? 'ml' : ''}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Apply Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-[#E6D5B8]/80 bg-white/60 p-3 rounded-xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs font-bold text-[#302621]">
                {selectedCount} dari {brandList.length} varian terpilih
              </span>
            </div>

            <Button
              type="button"
              onClick={handleApplyToInvoice}
              disabled={selectedCount === 0}
              className="bg-gradient-to-r from-[#8C6208] to-[#5A3F05] hover:from-[#6F4E06] hover:to-[#432F03] text-white text-xs font-bold px-5 h-9 rounded-xl shadow-sm gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Masukkan {selectedCount} Varian ke Form Invoice</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>

        </div>
      )}
    </div>
  );
}
